import React, { useEffect, useState, useCallback } from "react";
import { Section, Eyebrow, currency } from "@/components/Primitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Bed, Bath, Maximize, MapPin, TrendingUp, Plus } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const DEALS = ["All", "For Sale", "For Rent", "Investment"];
const TYPES = ["All", "Single Family", "Multi Family", "Condo"];

const RealEstate = () => {
  const { user } = useAuth();
  const [props, setProps] = useState([]);
  const [deal, setDeal] = useState("All");
  const [type, setType] = useState("All");
  const [location, setLocation] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ title: "", location: "", type: "Single Family", deal_type: "For Sale", price: "", arv: "", cap_rate: "", rehab_budget: "", beds: "", baths: "", sqft: "", image: "", description: "" });

  const load = useCallback(() => {
    api.get("/properties", { params: { deal_type: deal, type, location } }).then((r) => setProps(r.data)).catch(() => {});
  }, [deal, type, location]);
  useEffect(() => { load(); }, [load]);

  const connect = async (p) => {
    if (!user) { toast.error("Please log in to connect"); return; }
    try {
      const { data } = await api.post(`/properties/${p.id}/connect`);
      toast.success("Connection request sent to the realtor/investor!");
      if (data.requires_payment) {
        const { data: co } = await api.post("/payments/checkout", { lookup_key: data.lookup_key, origin_url: window.location.origin, context: `property:${p.id}` });
        window.location.href = co.checkout_url;
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not connect"); }
  };

  const create = async () => {
    if (!user) { toast.error("Please log in"); return; }
    try {
      await api.post("/properties", { ...form, price: Number(form.price), arv: Number(form.arv || 0), cap_rate: Number(form.cap_rate || 0), rehab_budget: Number(form.rehab_budget || 0), beds: Number(form.beds || 0), baths: Number(form.baths || 0), sqft: Number(form.sqft || 0) });
      toast.success("Property listed!");
      setModal(false); load();
    } catch (e) { toast.error("Could not list property"); }
  };

  return (
    <div>
      <div className="eti-grid-bg border-b border-white/10 py-14">
        <Section>
          <Eyebrow>Real Estate & Investor Hub</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white leading-[1.05]">Buy. Rent. Flip.</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">Connect with local and international realtors and investors. Every deal comes with built-in contractor rehab estimates.</p>
        </Section>
      </div>

      <Section className="py-10">
        <div className="flex flex-col lg:flex-row gap-3 mb-8">
          <Input placeholder="Search location..." value={location} onChange={(e) => setLocation(e.target.value)} className="bg-secondary border-white/10 flex-1" data-testid="realestate-location-input" />
          <Select value={deal} onValueChange={setDeal}><SelectTrigger className="bg-secondary border-white/10 w-full lg:w-44" data-testid="realestate-deal-filter"><SelectValue /></SelectTrigger><SelectContent>{DEALS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
          <Select value={type} onValueChange={setType}><SelectTrigger className="bg-secondary border-white/10 w-full lg:w-44" data-testid="realestate-type-filter"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
          <Button onClick={() => setModal(true)} className="bg-primary hover:bg-primary/90 text-white font-semibold" data-testid="realestate-list-button"><Plus className="h-4 w-4 mr-1" />List Property</Button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {props.map((p) => (
            <div key={p.id} className="eti-card rounded-xl overflow-hidden flex flex-col" data-testid={`property-card-${p.id}`}>
              <div className="relative h-48">
                <img src={p.image || "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=85"} alt={p.title} className="w-full h-full object-cover" />
                <Badge className="absolute top-3 left-3 bg-primary text-white border-0">{p.deal_type}</Badge>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h4 className="font-display font-bold text-white leading-tight">{p.title}</h4>
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><MapPin className="h-3 w-3" />{p.location}</p>
                <div className="flex gap-4 mt-3 text-xs text-slate-400">
                  {p.beds > 0 && <span className="flex items-center gap-1"><Bed className="h-3.5 w-3.5" />{p.beds}</span>}
                  {p.baths > 0 && <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" />{p.baths}</span>}
                  {p.sqft > 0 && <span className="flex items-center gap-1"><Maximize className="h-3.5 w-3.5" />{p.sqft} sf</span>}
                </div>
                {p.deal_type === "Investment" && (
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-secondary rounded p-2"><p className="text-slate-500">ARV</p><p className="text-white font-mono">{currency(p.arv)}</p></div>
                    <div className="bg-secondary rounded p-2"><p className="text-slate-500">Cap Rate</p><p className="text-primary font-mono flex items-center gap-1"><TrendingUp className="h-3 w-3" />{p.cap_rate}%</p></div>
                  </div>
                )}
                <div className="mt-auto pt-4 flex items-center justify-between border-t border-white/5 mt-4">
                  <span className="font-display text-xl font-bold text-white">{p.deal_type === "For Rent" ? `${currency(p.price)}/mo` : currency(p.price)}</span>
                  <Button size="sm" onClick={() => connect(p)} className="bg-primary hover:bg-primary/90 text-white font-semibold" data-testid={`property-connect-button-${p.id}`}>Connect</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="bg-card border-white/10 max-h-[90vh] overflow-y-auto eti-scroll" data-testid="list-property-modal">
          <DialogHeader><DialogTitle className="font-display uppercase">List a Property</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-slate-300">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="property-title-input" /></div>
            <div><Label className="text-xs text-slate-300">Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="property-location-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-300">Type</Label><Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger className="bg-secondary border-white/10 mt-1"><SelectValue /></SelectTrigger><SelectContent>{TYPES.filter((t) => t !== "All").map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs text-slate-300">Deal Type</Label><Select value={form.deal_type} onValueChange={(v) => setForm({ ...form, deal_type: v })}><SelectTrigger className="bg-secondary border-white/10 mt-1"><SelectValue /></SelectTrigger><SelectContent>{DEALS.filter((t) => t !== "All").map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs text-slate-300">Price</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="property-price-input" /></div>
              <div><Label className="text-xs text-slate-300">Beds</Label><Input type="number" value={form.beds} onChange={(e) => setForm({ ...form, beds: e.target.value })} className="bg-secondary border-white/10 mt-1" /></div>
              <div><Label className="text-xs text-slate-300">Baths</Label><Input type="number" value={form.baths} onChange={(e) => setForm({ ...form, baths: e.target.value })} className="bg-secondary border-white/10 mt-1" /></div>
            </div>
            <div><Label className="text-xs text-slate-300">Image URL (optional)</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="bg-secondary border-white/10 mt-1" /></div>
            <div><Label className="text-xs text-slate-300">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-secondary border-white/10 mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={create} className="bg-primary hover:bg-primary/90 text-white font-semibold w-full" data-testid="property-submit-button">List Property</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RealEstate;
